import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartAddPendingEvent,
  CartQuantityRefreshedEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== 'true') {
      const animationEnabled = this.dataset.addToCartAnimation === 'true';
      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
      this.animateAddToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {Array<{variantId: string, quantity: number, sellingPlan?: string}>} */
  #addToCartQueue = [];

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      this.#updateCartQuantityFromData(cart);
      // CARLYLE: this fetch is now routinely called WITHOUT being awaited by the add-to-cart flow
      // below (see #processAddToCart) so it never blocks the drawer opening. Dispatch a dedicated,
      // low-stakes event so the drawer's accessible live-region count can still catch up once the
      // real total is known — deliberately NOT `cart:update`/CartAddEvent (that name is shared and
      // re-triggering it here would re-open/re-announce as if this were a fresh add).
      document.dispatchEvent(new CartQuantityRefreshedEvent(cart));
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      // CARLYLE — G: dispatch even on failure (resource: undefined) so nothing that's waiting on
      // this event — the drawer's live-region count catch-up, announcement-shipping.js — waits
      // forever for a refresh that's never coming. The drawer-open path itself is unaffected: it
      // never depended on this fetch (see CartAddEvent dispatch above, and the CartErrorEvent fix
      // for the /cart/add.js request itself failing, which is the more critical failure mode).
      document.dispatchEvent(new CartQuantityRefreshedEvent(undefined));
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();
      const sellingPlan = this.#getSellingPlan();

      if (intendedVariantId) {
        // Seasonal Candle Box / Shopify Subscriptions: a variant change was still in-flight
        // when the user clicked add-to-cart, so this is queued for the batch endpoint below
        // instead of submitting the (stale) form directly. Reading `selling_plan` here too
        // (only added when a plan is actually selected) means a subscribe click can't
        // silently be converted into a one-off purchase by this race.
        const queuedItem = { variantId: intendedVariantId, quantity };
        if (sellingPlan) queuedItem.sellingPlan = sellingPlan;
        this.#addToCartQueue.push(queuedItem);
      }

      this.refs.addToCartButtonContainer?.animateAddToCart?.();
      return;
    }

    this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * Reads the currently-selected `selling_plan`, if any (Shopify Subscriptions).
   * Returns undefined for products with no selling plan groups, and when one-time
   * purchase is selected (every selling_plan input is empty in that case).
   *
   * CARLYLE (2026-08-20): the Shopify Subscriptions app block renders TWO copies of
   * `input[name="selling_plan"]` on a subscription PDP:
   *   1. `.selected-selling-plan-id` INSIDE the app block — i.e. outside
   *      `<product-form-component>`, so a form-scoped query can't see it;
   *   2. a copy of (1) the app injects into the product `<form>`.
   * (Until the same commit's follow-up, `blocks/buy-buttons.liquid` also rendered an
   * always-empty hidden input FIRST in DOM order for the retired `ca-subscribe-picker`
   * block, so a plain `querySelector` read "" and silently converted every subscribe
   * click into a one-time purchase. The input is gone; the non-empty scan below stays,
   * because it is also what reaches copy (1) at all.)
   * Take the first NON-EMPTY value in the container — the app clears
   * all of its copies when "One-time purchase" is selected, so "no non-empty value"
   * reads as one-time. Caveat: that also describes the app block before it has
   * hydrated, and the instant it is re-rendering on a variant switch — in those
   * windows this returns undefined and the add is a full-price one-time. The visible
   * UI is mid-render in the same windows, so it can't disagree with the cart, but
   * this is an assumption, not a guarantee.
   *
   * Widening past the form is load-bearing for a second reason: #onVariantUpdate
   * morphs the whole [ref="productFormButtons"] subtree on a shape change, and the
   * server HTML has no app input, so copy (3) can be morphed away mid-session.
   * Copy (2) lives outside the form and survives.
   *
   * SCOPE: the same `.shopify-section, dialog, product-card` chain connectedCallback
   * uses — NOT `.shopify-section || document`. The quick-add modal is rendered at
   * <body> level (layout/theme.liquid:212), outside every section, so a `document`
   * fallback there would scan the whole page and could pick up the PDP's plan id for
   * a different product. The per-form guard below is the second line of defence: if
   * an input belongs to another product form in the same container, skip it. (If a
   * second, theme-owned selling-plan picker is ever added alongside the app block,
   * revisit — first-non-empty-wins means one picker can't express one-time over a
   * stale value in the other.)
   * @returns {string | undefined}
   */
  #getSellingPlan() {
    const scope = this.closest('.shopify-section, dialog, product-card') || this;
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (scope.querySelectorAll('input[name="selling_plan"]'));

    for (const input of inputs) {
      if (input.disabled || !input.value) continue;
      // Inputs the app block renders outside any form have no owner — those are ours by
      // container. An input owned by a DIFFERENT product form belongs to another product.
      const owner = input.closest('product-form-component');
      if (owner && owner !== this) continue;
      return input.value;
    }

    return undefined;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  #processAddToCart(overrideVariantId, overrideQuantity, event) {
    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    if (!overrideVariantId) {
      const anyButtonDisabled = Array.from(allAddToCartContainers).some(
        (container) => container.refs.addToCartButton?.disabled
      );
      if (anyButtonDisabled) return;
    }

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    if (overrideVariantId) {
      formData.set('id', overrideVariantId);
    }
    if (overrideQuantity !== undefined) {
      formData.set('quantity', overrideQuantity.toString());
    }

    // CARLYLE — optimistic open (perf fix, 2026-07-26): tell the drawer to open NOW, before the
    // /cart/add.js request even goes out. Client-side validation above already passed, so this is
    // a real add. Measured field data showed ~2.3s from click to drawer-open, almost entirely
    // server-render + a redundant follow-up fetch (see the CartQuantityRefreshedEvent comment
    // below) — none of which the shopper needs to wait through with a closed drawer.
    this.dispatchEvent(new CartAddPendingEvent(this.id));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    // CARLYLE (2026-08-20): `append` used to sit INSIDE the loop, sending one `sections`
    // entry per cart-items-component, each a growing prefix of the same list. Both encodings
    // resolve duplicate keys last-wins so it worked, but the body below now goes through a
    // different parser — don't leave the drawer's re-render resting on that precedence.
    // Guarded on a non-empty list to preserve the old no-components behaviour: zero
    // cart-items-components meant the loop body never ran, so no `sections` key was sent.
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    // Multipart is the only encoding Shopify's cart-add pixel records; a selected selling plan
    // forces urlencoded instead, because an app fetch wrapper strips selling_plan from multipart.
    let body = formData;
    const sellingPlan = this.#getSellingPlan();

    if (sellingPlan) {
      const encoded = new URLSearchParams();
      let encodable = true;

      for (const [key, value] of formData.entries()) {
        if (key === 'selling_plan') continue;
        if (typeof value !== 'string') {
          encodable = false;
          break;
        }
        encoded.append(key, value);
      }

      if (encodable) {
        encoded.set('selling_plan', sellingPlan);
        body = encoded;
      }
    }

    const fetchCfg = fetchConfig('javascript', { body });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // CARLYLE — perf fix (2026-07-26): CartAddEvent used to `await` this /cart.js re-fetch
          // before dispatching, purely to attach an accurate total-quantity `resource` to the
          // event. That fetch was measured adding ~750-860ms between the /cart/add.js response
          // landing and the drawer actually opening (drawer-open is gated on CartAddEvent — see
          // cart-drawer.js #handleCartAdd), on top of the drawer content having nothing to do with
          // that fetch: component-cart-items.js morphs the drawer from `response.sections`, which
          // we already have synchronously right here.
          //
          // Fire the quantity refetch WITHOUT awaiting it (fire-and-forget) so it can't block the
          // dispatch below. Consumers that need the fresh cart total (the drawer's live-region
          // announce, "(N in cart)" labels on other instances of this product-form on the page) get
          // it a beat later via CartQuantityRefreshedEvent / the existing #onCartUpdate fallback —
          // both already handle a missing/undefined `resource` gracefully, so nothing here should
          // regress; it just arrives slightly after the drawer is already open instead of before.
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent(undefined, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        // CARLYLE — A: without this, a failed/offline /cart/add.js left the drawer stuck open in
        // the optimistic-open pending skeleton (aria-busy="true") forever — CartAddPendingEvent
        // opened it, but nothing ever told it the add didn't happen. CartErrorEvent already existed
        // for the server-returned-an-error case above; dispatch it here too for the network-failure
        // case, so cart-drawer.js's #handleCartError (→ #exitPendingState) always runs.
        this.dispatchEvent(new CartErrorEvent(this.id, 'There was an error adding this to your cart.', {}, {}));
      })
      .finally(() => {
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /** @param {Array<{variantId: string, quantity: number, sellingPlan?: string}>} items */
  #processBatchAddToCart(items) {
    if (items.length === 0) return;

    const { addToCartTextError } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);

    // CARLYLE — optimistic open (perf fix, 2026-07-26): same rationale as #processAddToCart —
    // this batch path fires when a variant change was still in flight when Add was clicked
    // (queued items), so open the drawer immediately rather than waiting on this fetch too.
    this.dispatchEvent(new CartAddPendingEvent(this.id));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    for (const item of cartItemsComponents) {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    }

    const payload = {
      items: items.map((item) => {
        /** @type {{id: number, quantity: number, selling_plan?: number}} */
        const cartItem = {
          id: Number(item.variantId),
          quantity: item.quantity,
        };
        // Only set when a plan was actually queued (see #getSellingPlan) — omitting the key
        // entirely for non-subscription items, rather than sending selling_plan: undefined.
        if (item.sellingPlan) cartItem.selling_plan = Number(item.sellingPlan);
        return cartItem;
      }),
      sections: cartItemComponentsSectionIds.join(','),
    };

    fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then(async (response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(this.id, response.message, response.description, response.errors)
          );

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = response.message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(response.message));
            }
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              addToCartTextError.classList.add('hidden');
              this.#clearLiveRegionText();
            }, ERROR_MESSAGE_DISPLAY_DURATION);
          }

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
              productId: this.dataset.productId,
            })
          );
          return;
        }

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        const allAddToCartContainers = /** @type {NodeListOf<AddToCartComponent>} */ (
          this.querySelectorAll('add-to-cart-component')
        );
        const anyAddToCartButton = allAddToCartContainers[0]?.refs.addToCartButton;
        if (anyAddToCartButton) {
          const addedTextElement = anyAddToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;
          this.#setLiveRegionText(addedText);
          setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
        }

        // CARLYLE — perf fix (2026-07-26): mirror of the fix in #processAddToCart above — don't
        // block the batch CartAddEvent (and therefore the drawer) on the /cart.js quantity refetch.
        this.#fetchAndUpdateCartQuantity();

        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
        this.dispatchEvent(
          new CartAddEvent(undefined, this.id, {
            source: 'product-form-component',
            itemCount: totalQuantity,
            productId: this.dataset.productId,
            sections: response.sections,
          })
        );
      })
      .catch((error) => {
        console.error(error);
        // CARLYLE — A: mirror of the fix in #processAddToCart's catch above — this batch path also
        // dispatches CartAddPendingEvent (see above) and needs the same failure signal.
        this.dispatchEvent(new CartErrorEvent(this.id, 'There was an error adding this to your cart.', {}, {}));
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // CARLYLE (2026-08-01): the back-in-stock fork in blocks/buy-buttons.liquid REPLACES both the
    // add-to-cart and the accelerated-checkout buttons with the notify-me form. So switching between
    // a sold-out and an in-stock variant changes the SHAPE of the buy box, not just the state of a
    // button that is always present — which is what every per-fragment patch below assumes.
    //
    // Without this, the guard beneath returns early (both refs are absent in the sold-out render) and
    // NOTHING restores the buy box. MEASURED on staging: arriving on a sold-out variant via deep link
    // (which is exactly what a Klaviyo back-in-stock email is) and switching to an in-stock variant
    // fired ZERO network requests on click and left the cart empty, while a fresh load of that same
    // variant added normally. On mobile it is worse than a missing button: sticky-add-to-cart.js
    // re-shows its bar because data-variant-available flips to true, so the shopper gets a button
    // that is visible, enabled, labelled "Add to cart" — and silently inert.
    //
    // When the shape changes, morph the whole [ref="productFormButtons"] subtree. That is the same
    // move the quantity-rules branch below already makes for the same reason (a fragment appearing or
    // disappearing rather than changing), and it covers both directions of the fork. Return
    // afterwards: the wholesale morph supersedes every per-fragment patch that follows, and those
    // patches hold stale references to nodes it has just replaced.
    const newAddToCartButtonForShape = event.detail.data.html.querySelector(
      'product-form-component [ref="addToCartButton"]'
    );
    const shapeChanged = !!newAddToCartButtonForShape !== !!currentAddToCartButton;

    // Sold-out -> a DIFFERENT sold-out variant does not change the shape (notify-me both times), so
    // the check above misses it — but the notify form is still bound to the old variant. Left alone,
    // a shopper on an all-sold-out product picks "Fig", signs up, and Klaviyo notifies them about
    // "Cedar". Silent, and permanent for that session.
    const newBackInStock = event.detail.data.html.querySelector('product-form-component ca-back-in-stock');
    const currentBackInStock = this.querySelector('ca-back-in-stock');
    const notifyNeedsRebind =
      !!newBackInStock &&
      !!currentBackInStock &&
      newBackInStock.getAttribute('data-variant-id') !== currentBackInStock.getAttribute('data-variant-id');

    if (shapeChanged || notifyNeedsRebind) {
      const { productFormButtons } = this.refs;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');
      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);
        return;
      }
    }

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
        // Preorder line: always present (empty when not a preorder), so this is always the
        // in-place morph path — the ship-date text + line-item properties update per variant.
        ['.ca-preorder-line', this.refs.preorderLine, this.refs.productFormButtons],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
